<template>
  <div class="upload-container">
    <div class="upload-box">
      <input
        :id="id"
        ref="input"
        type="file"
        :name="name"
        :disabled="disabled"
        :accept="accept"
        class="upload-input-hidden"
        @change="onFileChange"
        @focus="isFocused = true"
        @blur="onBlur"
      />

      <div
        class="upload-visible-box"
        :class="{ 'upload-error': props.error }"
        @click="input?.click()"
      >
        <div class="upload-label-with-tooltip">
          <div
            v-if="tooltip"
            class="upload-tooltip-wrapper"
            @mouseenter="showTooltip = true"
            @mouseleave="showTooltip = false"
          >
            <p-icon icon="info" color="fg-tertiary" size="sm" />
            <div v-if="showTooltip" class="upload-tooltip-content">
              {{ tooltip }}
            </div>
          </div>
          <span class="upload-placeholder">{{ fileName || label }}</span>
        </div>

        <div v-if="maxSize" class="upload-meta">
          <span class="upload-max">Max: {{ maxSize }}MB</span>
          <p-icon icon="attachment-clip-hook" color="fg-tertiary" size="sm" />
        </div>
      </div>
    </div>

    <p v-if="assistiveText" class="f-utility-14 t-fg-tertiary">
      {{ assistiveText }}
    </p>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch } from 'vue'
import { useField } from 'vee-validate'

const props = defineProps({
  id: {
    type: String,
    default: '',
  },
  name: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    default: '',
  },
  tooltip: {
    type: String,
    default: '',
  },
  accept: {
    type: String,
    default: '',
  },
  modelValue: {
    type: [File, null],
    default: null,
  },
  assistiveText: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  maxSize: {
    type: Number,
    default: 0,
  },
  error: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue'])

const isFocused = ref(false)
const showTooltip = ref(false)
const input = ref<HTMLInputElement | null>(null)

const fileName = computed(() => (props.modelValue as File | null)?.name || '')

const { value, validate } = useField<File | null>(props.name, undefined, {
  initialValue: props.modelValue,
})

watch(
  () => props.modelValue,
  () => {
    value.value = props.modelValue
  },
)

function onFileChange(event: Event) {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0] || null
  emit('update:modelValue', file)
  value.value = file
  validate()
}

function onBlur() {
  isFocused.value = false
  validate()
}
</script>
